/**
 * Lv6 達人 AI 用の決定的疑似乱数生成器（PRNG）
 *
 * 用途:
 * - Web Worker は関数値（Math.random）を越境できないため、seed（数値）を渡して
 *   Worker 内で同じ乱数列を再生成する必要がある
 * - テストの決定性確保（同 seed → 同結果）
 *
 * 暗号用途には使えないが、ゲーム AI の乱数用途には十分。
 */

/**
 * Mulberry32 PRNG
 *
 * 32bit seed から決定的な乱数列 [0, 1) を生成する。
 * 周期は 2^32（バトル1試合 ~30ターンには十分）。
 *
 * @param seed 32bit 符号なし整数として扱う（負値も unsigned 化）
 * @returns 呼び出すたびに次の乱数を返す関数
 */
export function mulberry32(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6D2B79F5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * 新しい seed を生成する
 *
 * Math.random() と Date.now() を組み合わせて 32bit unsigned 整数を作る。
 * テスト時は引数で固定乱数を注入できる。
 *
 * @param baseRandom 基底の乱数関数（既定: Math.random）
 * @returns 32bit unsigned 整数（0 以上 2^32 未満）
 */
export function generateSeed(baseRandom: () => number = Math.random): number {
  const randomPart = Math.floor(baseRandom() * 0x100000000);
  const timePart = Date.now() & 0xFFFFFFFF;
  return (randomPart ^ timePart) >>> 0;
}
